/* eslint-disable  */
import { FineUploaderBasic } from 'fine-uploader/lib/core';
import { useRef } from 'react';

import { useToast } from 'components/molecules/Toast';
import { refreshTokenChatbotService } from 'services/accountConversations';
import {
  getAccessToken,
  getLocalStorage,
  setLocalStorage,
} from 'services/common/storage';
import {
  LOCAL_STORAGE_CHATBOT,
  MAX_SIZE_UPLOAD
} from 'utils/constants';
import { formatBytes } from 'utils/functions';

type Props = {
  conversationCode: string;
  handleChunkLoading?: (isLoading: boolean) => void;
  handleChooseFile(file: UploadFileType): void;
  allowedExtensions?: Array<string>;
  forAgent?: boolean;
  code?: string;
};

const useUploadFileService = ({
  conversationCode,
  allowedExtensions,
  handleChunkLoading,
  handleChooseFile,
  forAgent,
  code,
}: Props) => {
  const fileRef = useRef<File | undefined>(undefined);

   const botId = (window as any).currentBotPath;
  const token = forAgent
    ? getAccessToken()
    : getLocalStorage(LOCAL_STORAGE_CHATBOT.ACCESSTOKEN_CHATBOT+botId);
  const { showToast } = useToast();

  // Init File Uploader
  const uploader = new FineUploaderBasic({
    autoUpload: false,
    request: {
      endpoint: forAgent
        ? `api/v1/agents/${code}/conversations/${conversationCode}/upload-chunk`
        : `api/v1/account-conversations/${conversationCode}/upload-chunk`,
      customHeaders: {
        Authorization: `Bearer ${token}`,
      },
    },
    validation: {
      allowedExtensions: allowedExtensions,
      sizeLimit: MAX_SIZE_UPLOAD,
    },
    multiple: true,
    chunking: {
      enabled: true,
      mandatory: true,
      partSize: 1000000, // 1MB,
      success: {
        jsonPayload: true,
        endpoint: forAgent
          ? `/api/v1/agents/${code}/conversations/${conversationCode}/merge-chunk`
          : `api/v1/account-conversations/${conversationCode}/merge-chunk`,
        params(id: number) {
          const uuid = uploader.getUuid(id);
          const { name } = uploader.getFile(id) as File;
          return {
            chunkedUuid: uuid,
            fileExtension:
              fileRef.current?.type &&
              fileRef.current?.type?.split('/')?.length > 1
                ? fileRef.current?.type.split('/')[1]
                : fileRef.current?.type,
            qqfilename: fileRef.current?.name || name,
          };
        },
      },
    },
    callbacks: {
      onSubmit(id: number, name: string) {
        const file = uploader.getFile(id);
        handleChooseFile({
          id: uploader.getUuid(id),
          file,
          status: 'loading',
        });
        handleChunkLoading?.(true);
        if (file && file.size > MAX_SIZE_UPLOAD) {
          showToast({
            message: `Kích thước file vượt quá giới hạn. Vui lòng tải file nhỏ hơn: ${formatBytes(MAX_SIZE_UPLOAD)}`,
            show: true,
            type: 'error',
          });
          uploader.cancel(id); // Cancel upload if file is too large
          handleChunkLoading?.(true);
          return false;
        }
      },
      onComplete(
        id: number,
        name: string,
        res: MergeChunkResponse,
        xhr: XMLHttpRequest,
      ) {
        if (!res.success) {
          return;
        }
        const file = uploader.getFile(id);
        handleChooseFile({
          id: uploader.getUuid(id),
          file,
          status: 'success',
          fileSubmit: res?.data,
        });
        handleChunkLoading?.(false);
      },
      onError: async (
        id: number,
        name: string,
        errorReason: string,
        xhr: XMLHttpRequest,
      ) => {
        const file = uploader.getFile(id);
        handleChooseFile({
          id: uploader.getUuid(id),
          file,
          status: 'error',
        });
        handleChunkLoading?.(false);
        if (xhr?.status === 401) {
          await refreshTokenChatbotService().then((data) => {
            setLocalStorage(
              LOCAL_STORAGE_CHATBOT.ACCESSTOKEN_CHATBOT + botId,
              data.accessToken,
            );
            setLocalStorage(
              LOCAL_STORAGE_CHATBOT.REFRESHTOKEN_CHATBOT,
              data.refreshToken,
            );
            uploader.setCustomHeaders({
              Authorization: `Bearer ${data.accessToken}`,
            });
            uploader.retry(id);
          });
          return;
        }
        if (id !== null) {
          const response =
            typeof xhr?.response === 'string' && xhr?.status !== 413
              ? JSON.parse(xhr.response)
              : undefined;
          let errorText = '';
          if (
            response &&
            response?.errors &&
            Array.isArray(response?.errors) &&
            response?.errors?.length > 0
          ) {
            errorText = response?.errors[0]?.message;
          } else if (xhr.statusText) {
            errorText = xhr.statusText;
          } else {
            errorText = errorReason || 'Upload không thành công';
          }

          showToast({
            message: errorText,
            show: true,
            type: 'error',
          });
        }
        return;
      },
    },
  });

  return {
    uploader,
  };
};

export default useUploadFileService;
